// Sidebar Column Options Component

import React, { Component } from 'react'
import update from 'react-addons-update'
import PropTypes from 'prop-types'
import ReactDOM from 'react-dom'
import { DragSource, DropTarget } from 'react-dnd'
import classnames from 'classnames'
import _ from 'lodash'

import SidebarHeader from '../../../layout/sidebar_header/sidebar_header.jsx'
import IntlMessage from '../../../../common/common/intl/intl_message.jsx'
import IconButton from '../../../common/icon_button/icon_button.jsx'

const SidebarColumnTypes = {
  COLUMN: 'column'
}

class SidebarColumn extends Component {
  static get propTypes () {
    return {
      index: PropTypes.number,
      column: PropTypes.object,
      title: PropTypes.string,
      isDragging: PropTypes.bool,
      onSelect: PropTypes.func,
      onMove: PropTypes.func,
      connectDropTarget: PropTypes.func,
      connectDragSource: PropTypes.func
    }
  }

  static get defaultProps () {
    return {
      index: 0,
      column: null,
      title: '',
      isDragging: false,
      onSelect: null,
      onMove: null,
      connectDropTarget: null,
      connectDragSource: null
    }
  }

  _onSelect = () => {
    const { onSelect, column } = this.props
    if (onSelect) {
      onSelect(column)
    }
  }

  render () {
    const {
      column,
      title,
      connectDragSource,
      connectDropTarget,
      isDragging
    } = this.props
    const className = classnames('content-item', {
      disabled: column.disabled,
      selected: column.selected,
      dragging: isDragging
    })
    return connectDragSource(
      connectDropTarget(
        <div
          className={className}
          onClick={column.disabled || isDragging ? null : this._onSelect}
        >
          <div className='title'>
            <IntlMessage ident={title} />
          </div>
          <IconButton
            icon='drag'
            size='big'
            type='normal'
            className='actioncont'
          />
        </div>
      )
    )
  }
}

const SidebarColumnDrop = DropTarget(
  SidebarColumnTypes.COLUMN,
  {
    hover (props, monitor, component) {
      if (!component) {
        return null
      }

      const dragIndex = monitor.getItem().index
      const hoverIndex = props.index
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return
      }
      // Determine rectangle on screen
      const hoverBoundingRect = ReactDOM.findDOMNode(
        component
      ).getBoundingClientRect()

      // Get vertical middle
      const hoverMiddleX =
        (hoverBoundingRect.right - hoverBoundingRect.left) / 2
      // Determine mouse position
      const clientOffset = monitor.getClientOffset()
      // Get pixels to the top
      const hoverClientX = clientOffset.x - hoverBoundingRect.left
      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientX < hoverMiddleX) {
        return
      }
      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientX > hoverMiddleX) {
        return
      }
      // Time to actually perform the action
      props.onMove(dragIndex, hoverIndex)
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      monitor.getItem().index = hoverIndex
    }
  },
  connect => ({
    connectDropTarget: connect.dropTarget()
  })
)(
  DragSource(
    SidebarColumnTypes.COLUMN,
    {
      beginDrag: props => ({
        id: props.id,
        index: props.index
      })
    },
    (connect, monitor) => ({
      connectDragSource: connect.dragSource(),
      isDragging: monitor.isDragging()
    })
  )(SidebarColumn)
)

class SidebarColumnOptions extends Component {
  constructor (props) {
    super(props)

    this.state = {
      columns: _.cloneDeep(props.columns || []),
      dataDate: new Date()
    }
  }

  static get propTypes () {
    return {
      columns: PropTypes.array,
      onClose: PropTypes.func,
      onGetTitle: PropTypes.func
    }
  }

  static get defaultProps () {
    return {
      columns: [],
      onClose: null,
      onGetTitle: null
    }
  }

  componentDidUpdate (prevProps) {
    if (!_.isEqual(prevProps.columns, this.props.columns)) {
      this.setState({ columns: _.cloneDeep(this.props.columns) })
    }
  }

  _onMoveColumn = (dragIndex, hoverIndex) => {
    const { columns } = this.state
    const dragColumn = columns[dragIndex]

    this.setState(
      update(this.state, {
        columns: {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, dragColumn]
          ]
        }
      })
    )
  }

  _onPushColumn = column => {
    this.setState(
      update(this.state, {
        columns: {
          $push: [column]
        }
      })
    )
  }

  _onRemoveColumn = index => {
    this.setState(
      update(this.state, {
        columns: {
          $splice: [[index, 1]]
        }
      })
    )
  }

  _onSelectColumn (column) {
    column.selected = !column.selected

    const { columns } = this.state
    this.setState({ columns, dataDate: new Date() })
  }

  _onClose = () => {
    const { onClose } = this.props
    if (onClose) {
      onClose(this.state.columns)
    }
  }

  _renderHeader () {
    return (
      <SidebarHeader
        icon='settings'
        title='project.budgetContent.columnHeaderTitle'
        onClose={this._onClose} // eslint-disable-line react/jsx-handler-names
        showCloseBorder
      />
    )
  }

  _renderColumn (column, columnIndex) {
    const { onGetTitle } = this.props
    const title = onGetTitle ? onGetTitle(column.ident) : ''
    return (
      <SidebarColumnDrop
        key={column.ident}
        column={column}
        index={columnIndex}
        title={title}
        onSelect={this._onSelectColumn.bind(this, column)}
        onMove={this._onMoveColumn} // eslint-disable-line react/jsx-handler-names
        onRemove={this._onRemoveColumn} // eslint-disable-line react/jsx-handler-names
      />
    )
  }

  _renderColumns () {
    const { columns } = this.state
    if (!columns || columns.length === 0) {
      return null
    }

    return (
      <>{columns.map((column, index) => this._renderColumn(column, index))}</>
    )
  }

  _renderContent () {
    return (
      <div className='sidebar-maincontent'>
        <div className='sidebar-contentheader'>
          <div className='content-image'>
            <IconButton
              icon='colums'
              size='big'
              type='normal'
              className='actioncolumn'
            />
          </div>
          <div className='content-title'>
            <div className='content-maintitle'>
              <IntlMessage ident='project.budgetContent.columnRearrangeTitle' />
            </div>
            <div className='content-subtitle'>
              <IntlMessage ident='project.budgetContent.columnSubTitle' />
            </div>
          </div>
        </div>
        <div className='sidebar-content'>{this._renderColumns()}</div>
      </div>
    )
  }

  render () {
    return (
      <div className='sidebar-columnsetting'>
        <div className='sidebaroverlay' />
        <div className='sidebarwrap'>
          {this._renderHeader()}
          {this._renderContent()}
        </div>
      </div>
    )
  }
}

export default SidebarColumnOptions
